import React, { useState, useContext, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { BagContext } from '../context/BagContext';
import { useOrders } from '../context/OrderContext';
import { useNavigate } from 'react-router-dom';
import { cartService, orderService } from '../services/api';
import ConfirmationModal from '../components/ConfirmationModal';
import TrackingModal from '../components/TrackingModal';
import SiteLoading from '../components/SiteLoading';
import { PLACEHOLDER_NO_IMAGE_TINY } from '../utils/placeholderImage';
import { getPrimaryProductImageUrl } from '../utils/productImages';
import './OrdersPage.css';

// Map backend status (lowercase) to display format for tracking
const STATUS_TO_DISPLAY = {
  pending: 'Order Placed',
  confirmed: 'Order Confirmed',
  processing: 'Processing',
  packed: 'Packed',
  dispatched: 'Dispatched',
  'in transit': 'In Transit',
  'out for delivery': 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  shipped: 'Dispatched'
};

const TRACKING_STEP_ORDER = ['Order Placed', 'Order Confirmed', 'Processing', 'Packed', 'Dispatched', 'In Transit', 'Out for Delivery', 'Delivered'];

const transformOrderForTracking = (order) => {
  const rawStatus = (order.status || 'pending').toLowerCase().trim();
  const displayStatus = STATUS_TO_DISPLAY[rawStatus] || order.status || 'Order Placed';
  let trackingSteps = order.trackingSteps;
  if (!trackingSteps || !Array.isArray(trackingSteps) || trackingSteps.length === 0) {
    if (rawStatus === 'cancelled') {
      trackingSteps = ['Order Placed', 'Cancelled'];
    } else {
      const idx = TRACKING_STEP_ORDER.indexOf(displayStatus);
      trackingSteps = idx >= 0 ? TRACKING_STEP_ORDER.slice(0, idx + 1) : ['Order Placed'];
    }
  } else {
    trackingSteps = trackingSteps.map(s => STATUS_TO_DISPLAY[String(s || '').toLowerCase()] || s);
  }
  return {
    ...order,
    id: order._id || order.id,
    orderNumber: order.orderNumber || `ORDER-${(order._id || order.id)?.toString().slice(-8) || 'UNKNOWN'}`,
    date: order.createdAt || order.orderDate || order.date || new Date().toISOString(),
    status: displayStatus,
    total: order.total || 0,
    items: order.items?.map(item => ({
      name: item.name || item.product?.name || 'Unknown Product',
      image: item.image || getPrimaryProductImageUrl(item.product) || PLACEHOLDER_NO_IMAGE_TINY,
      size: item.size || 'N/A',
      color: item.color || 'N/A',
      quantity: item.quantity || 1,
      price: item.price || 0,
      productId: item.product?._id || item.product?.id || item.product || ''
    })) || [],
    paymentMethod: order.paymentMethod || 'N/A',
    paymentStatus: order.paymentStatus || 'pending',
    shippingAddress: order.shippingAddress,
    deliveredDate: order.deliveredAt || order.deliveredDate,
    trackingSteps
  };
};

const OrdersPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { bagItems: guestBagItems, removeFromBag, addToBag } = useContext(BagContext);
  const { orderStatusSystem } = useOrders();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All Orders');
  const [searchQuery, setSearchQuery] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingOrderLoading, setTrackingOrderLoading] = useState(false);
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  // Fetch cart for authenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      setCart(null);
      return;
    }

    const fetchCart = async () => {
      try {
        setCartLoading(true);
        const response = await cartService.getCart();
        if (response.success && response.data) {
          setCart(response.data);
        }
      } catch (error) {
        console.error('Error fetching cart:', error);
      } finally {
        setCartLoading(false);
      }
    };

    fetchCart();
  }, [isAuthenticated]);

  // Fetch orders from backend API
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setOrders([]);
      setOrdersLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        setOrdersLoading(true);
        setOrdersError(null);
        const response = await orderService.getUserOrders();
        
        if (response.success && response.data) {
          const transformedOrders = response.data.map(order => transformOrderForTracking(order));
          
          // Sort by date (newest first)
          transformedOrders.sort((a, b) => new Date(b.date) - new Date(a.date));
          
          setOrders(transformedOrders);
        } else {
          setOrdersError(response.error || 'Failed to load orders');
          setOrders([]);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        setOrdersError(error.message || 'Failed to load orders');
        setOrders([]);
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, user]);

  // Listen for order placement events to refresh orders
  useEffect(() => {
    const handleOrderPlaced = () => {
      // Refresh orders when a new order is placed
      if (isAuthenticated && user) {
        const fetchOrders = async () => {
          try {
            const response = await orderService.getUserOrders();
            if (response.success && response.data) {
              const transformedOrders = response.data.map(order => transformOrderForTracking(order));
              transformedOrders.sort((a, b) => new Date(b.date) - new Date(a.date));
              setOrders(transformedOrders);
            }
          } catch (error) {
            console.error('Error refreshing orders:', error);
          }
        };
        fetchOrders();
      }
    };

    window.addEventListener('orderPlaced', handleOrderPlaced);
    return () => window.removeEventListener('orderPlaced', handleOrderPlaced);
  }, [isAuthenticated, user]);

  // Helper function to extract image URL
  const getImageUrl = (imageData) => {
    if (!imageData) return '';
    if (typeof imageData === 'string') return imageData;
    if (Array.isArray(imageData) && imageData.length > 0) {
      return typeof imageData[0] === 'string' ? imageData[0] : imageData[0].url || '';
    }
    if (imageData.url) return imageData.url;
    return '';
  };

  // Get pending items - authenticated users get cart items, guests get bag items
  const getPendingItems = () => {
    if (isAuthenticated && cart?.items && cart.items.length > 0) {
      return cart.items.map(item => {
        const productImage = item.product?.images || item.product?.image;
        const imageUrl = getImageUrl(productImage);
        
        return {
          id: item._id,
          itemId: item._id,
          name: item.product.name,
          size: item.size || 'N/A',
          color: item.color || 'N/A',
          quantity: item.quantity,
          price: item.price,
          image: imageUrl || PLACEHOLDER_NO_IMAGE_TINY,
          productId: item.product._id || item.product.id
        };
      });
    } else if (!isAuthenticated && guestBagItems && guestBagItems.length > 0) {
      return guestBagItems.map(item => ({
        id: item.itemId || item.id,
        itemId: item.itemId || item.id,
        name: item.name,
        size: item.selectedSize || 'N/A',
        color: item.selectedColor || 'N/A',
        quantity: item.quantity,
        price: item.price,
        image: item.image || PLACEHOLDER_NO_IMAGE_TINY,
        productId: item.id
      }));
    }
    return [];
  };

  const pendingItems = getPendingItems();

  const filterOptions = ['All Orders', 'Last 30 Days', 'Last 6 Months', 'Last Year', 'Pending', 'Processing', 'Shipped', 'Delivered', 'Cancelled'];

  // Use orders from API
  const userOrders = orders;

  const filteredOrders = userOrders.filter(order => {
    // Time-based filters
    const orderDate = new Date(order.date);
    const now = new Date();
    let timeMatch = true;
    
    if (activeFilter === 'Last 30 Days') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      timeMatch = orderDate >= thirtyDaysAgo;
    } else if (activeFilter === 'Last 6 Months') {
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      timeMatch = orderDate >= sixMonthsAgo;
    } else if (activeFilter === 'Last Year') {
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      timeMatch = orderDate >= oneYearAgo;
    }
    
    // Status-based filters - map backend + display statuses to filter options
    const statusMap = {
      'Pending': ['pending', 'order placed'],
      'Processing': ['processing', 'confirmed', 'order confirmed', 'packed', 'dispatched', 'in transit', 'out for delivery'],
      'Shipped': ['shipped', 'dispatched', 'in transit', 'out for delivery'],
      'Delivered': ['delivered'],
      'Cancelled': ['cancelled', 'refunded']
    };
    
    let statusMatch = true;
    if (activeFilter === 'All Orders' || activeFilter.includes('Last')) {
      statusMatch = true;
    } else if (statusMap[activeFilter]) {
      statusMatch = statusMap[activeFilter].includes(order.status.toLowerCase());
    } else {
      statusMatch = order.status.toLowerCase() === activeFilter.toLowerCase();
    }
    
    // Search query filter
    const searchMatch = searchQuery === '' || 
                       (order.orderNumber && order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
                       order.items.some(item => 
                         (item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (item.color && item.color.toLowerCase().includes(searchQuery.toLowerCase()))
                       );
    
    return timeMatch && statusMatch && searchMatch;
  });

  const calculatePendingTotal = () => {
    if (isAuthenticated && cart) {
      return cart.subtotal || 0;
    }
    return pendingItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  // Handle clear all items
  const handleClearAll = async () => {
    if (isAuthenticated) {
      try {
        setCartLoading(true);
        await cartService.clearCart();
        setCart(null);
      } catch (error) {
        console.error('Error clearing cart:', error);
      } finally {
        setCartLoading(false);
      }
    } else {
      // For guests, clear all bag items
      pendingItems.forEach(item => {
        if (removeFromBag) {
          removeFromBag(item.itemId || item.id);
        }
      });
    }
  };

  // Confirmation modal functions
  const handleRemoveItem = (itemId) => {
    setItemToRemove(itemId);
    setShowConfirmation(true);
  };

  const confirmRemoveItem = async () => {
    if (itemToRemove) {
      if (isAuthenticated) {
        // Remove from API cart
        try {
          setCartLoading(true);
          await cartService.removeFromCart(itemToRemove);
          // Refresh cart
          const response = await cartService.getCart();
          if (response.success && response.data) {
            setCart(response.data);
          }
        } catch (error) {
          console.error('Error removing item from cart:', error);
        } finally {
          setCartLoading(false);
        }
      } else if (removeFromBag) {
        // Remove from guest bag
        removeFromBag(itemToRemove);
      }
      setShowConfirmation(false);
      setItemToRemove(null);
    }
  };

  const cancelRemoveItem = () => {
    setShowConfirmation(false);
    setItemToRemove(null);
  };

  // Reorder function - Navigate to product page
  const handleReorder = (item) => {
    console.log('Reorder clicked for item:', item);
    if (item.url) {
      // Use the new SEO-friendly route format
      navigate(`/${item.url}`);
    } else if (item.productId) {
      // Fallback to legacy product ID route
      navigate(`/product/${item.productId}`);
    } else {
      console.error('No URL or product ID found for item:', item);
    }
  };

  // Track order function - Fetch fresh order and open tracking modal
  const handleTrackOrder = async (order) => {
    const orderId = order.id || order._id;
    setShowTrackingModal(true);
    setSelectedOrder(transformOrderForTracking(order));
    setTrackingOrderLoading(true);
    try {
      const response = await orderService.getOrder(orderId);
      if (response.success && response.data) {
        const freshOrder = transformOrderForTracking(response.data);
        setSelectedOrder(freshOrder);
        setOrders(prev => prev.map(o => String(o.id || o._id) === String(orderId) ? freshOrder : o));
      }
    } catch (err) {
      console.error('Error fetching order for tracking:', err);
    } finally {
      setTrackingOrderLoading(false);
    }
  };

  // Close tracking modal
  const closeTrackingModal = () => {
    setShowTrackingModal(false);
    setSelectedOrder(null);
  };

  // Check if order can be deleted (only when status is Order Placed / pending)
  const canDeleteOrder = (order) => {
    const status = (order.status || '').toLowerCase();
    return status === 'pending' || status === 'order placed';
  };

  // Delete/Cancel order - only works when status is Order Placed
  const handleDeleteOrder = async (order) => {
    if (!canDeleteOrder(order)) return;
    const orderId = order.id || order._id;
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setDeletingOrderId(orderId);
    try {
      const response = await orderService.cancelOrder(orderId);
      if (response?.success) {
        // Refresh orders list (order will show as Cancelled)
        const res = await orderService.getUserOrders();
        if (res.success && res.data) {
          const transformed = res.data.map(o => transformOrderForTracking(o));
          transformed.sort((a, b) => new Date(b.date) - new Date(a.date));
          setOrders(transformed);
        }
      } else {
        throw new Error(response?.error || response?.message || 'Failed to cancel order');
      }
    } catch (err) {
      console.error('Delete order error:', err);
      alert(err?.message || 'Failed to cancel order. Please try again.');
    } finally {
      setDeletingOrderId(null);
    }
  };

  // Checkout functions
  const handleCheckoutItem = (item) => {
    // For authenticated users, navigate directly to checkout
    // For guests, add to bag first then navigate
    if (isAuthenticated) {
      navigate('/checkout');
    } else {
      // Add item to bag if not already there
      const existingItem = guestBagItems.find(bagItem => 
        bagItem.name === item.name && 
        bagItem.selectedColor === item.color &&
        bagItem.selectedSize === item.size
      );

      if (!existingItem) {
        const bagItem = {
          itemId: `${item.id}_${Date.now()}`,
          name: item.name,
          price: item.price,
          image: item.image,
          selectedColor: item.color,
          selectedSize: item.size,
          quantity: item.quantity || 1
        };
        addToBag(bagItem);
      }

      // Navigate to checkout page
      navigate('/checkout');
    }
  };

  const handleCheckoutAll = () => {
    // For authenticated users, navigate directly to checkout
    if (isAuthenticated) {
      navigate('/checkout');
    } else {
      // Add all pending items to bag for guests
      pendingItems.forEach(item => {
        const existingItem = guestBagItems.find(bagItem => 
          bagItem.name === item.name && 
          bagItem.selectedColor === item.color &&
          bagItem.selectedSize === item.size
        );

        if (!existingItem) {
          const bagItem = {
            itemId: `${item.id}_${Date.now()}`,
            name: item.name,
            price: item.price,
            image: item.image,
            selectedColor: item.color,
            selectedSize: item.size,
            quantity: item.quantity || 1
          };
          addToBag(bagItem);
        }
      });

      // Navigate to checkout page
      navigate('/checkout');
    }
  };

  // Handle remove item from cart
  const handleRemoveFromCart = async (itemId) => {
    if (isAuthenticated) {
      try {
        setCartLoading(true);
        await cartService.removeFromCart(itemId);
        // Refresh cart
        const response = await cartService.getCart();
        if (response.success && response.data) {
          setCart(response.data);
        }
      } catch (error) {
        console.error('Error removing item from cart:', error);
      } finally {
        setCartLoading(false);
      }
    } else {
      setItemToRemove(itemId);
      setShowConfirmation(true);
    }
  };

  // Show login message if user is not authenticated
  if (!user) {
    return (
      <div className="orders-page">
        <div className="container">
          <div className="auth-required">
            <div className="auth-illustration">
              <div className="auth-icon">📦</div>
              <div className="auth-decoration">
                <div className="decoration-circle circle-1"></div>
                <div className="decoration-circle circle-2"></div>
                <div className="decoration-circle circle-3"></div>
              </div>
            </div>
            
            <div className="auth-content">
              <h2>Track Your Orders!</h2>
              <p className="auth-message">
                To view your order history, track deliveries, and manage your purchases, 
                please sign in to your account or create a new one.
              </p>
              
              <div className="auth-features">
                <div className="feature-item">
                  <span className="feature-icon">📋</span>
                  <span>View Order History</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">🚚</span>
                  <span>Track Deliveries</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">📱</span>
                  <span>Order Updates</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">💳</span>
                  <span>Payment History</span>
                </div>
              </div>
            </div>
            
            <div className="auth-actions">
              <button 
                className="login-btn primary"
                onClick={() => navigate('/login')}
              >
                <span className="btn-icon">🔑</span>
                <span className="btn-text">Login Again</span>
              </button>
              <button 
                className="login-btn secondary"
                onClick={() => navigate('/signup')}
              >
                <span className="btn-icon">✨</span>
                <span className="btn-text">Create Account</span>
              </button>
            </div>
            
            <div className="auth-footer">
              <p>New to Super Wear? Join our community today!</p>
              <div className="auth-benefits">
                <span>🚀</span>
                <span>Exclusive Offers</span>
                <span>•</span>
                <span>🎁</span>
                <span>Welcome Bonus</span>
                <span>•</span>
                <span>⭐</span>
                <span>Premium Support</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="container">
        {/* Header Section */}
        <div className="orders-header">
          <h1 className="page-title">MY ORDERS</h1>
          <p className="page-subtitle">Track and manage your order history</p>
        </div>

        {/* Pending Orders Section */}
        {pendingItems.length > 0 && (
          <div className="pending-section">
            <div className="pending-header">
              <i className="fas fa-exclamation-circle"></i>
              <span>Pending for Payment - Checkout Items</span>
            </div>
            <div className="pending-content">
              <span className="pending-message">Complete your purchase</span>
              <div className="pending-actions">
                <button 
                  className="clear-all-btn" 
                  onClick={handleClearAll}
                  disabled={cartLoading}
                >
                  <i className="fas fa-trash"></i>
                  {cartLoading ? 'CLEARING...' : 'CLEAR ALL'}
                </button>
                <button 
                  className="checkout-all-btn" 
                  onClick={handleCheckoutAll}
                  disabled={cartLoading}
                >
                  <i className="fas fa-shopping-cart"></i>
                  CHECKOUT ALL (PKR {calculatePendingTotal().toLocaleString()})
                </button>
              </div>
            </div>

            {/* Pending Items List */}
            <div className="pending-items">
              {pendingItems.map((item, index) => (
                <div key={index} className="pending-item">
                  <div className="pending-item-row">
                    <img src={item.image} alt={item.name} className="pending-item-image" />
                    <div className="pending-item-details">
                      <h4>{item.name}</h4>
                      <p>Size: {item.size} | Color: {item.color} | Qty: {item.quantity}</p>
                      <div className="pending-item-price">Rs. {item.price.toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="pending-item-buttons">
                    <button 
                      className="pending-checkout-btn"
                      onClick={() => handleCheckoutItem(item)}
                    >
                      <i className="fas fa-shopping-cart"></i>
                      <span>CHECKOUT</span>
                    </button>
                    <button 
                      className="pending-remove-btn"
                      onClick={() => handleRemoveFromCart(item.itemId || item.id)}
                      disabled={cartLoading}
                    >
                      <i className="fas fa-trash-alt"></i>
                      <span>{cartLoading ? 'REMOVING...' : 'REMOVE'}</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order History Section */}
        <div className="order-history-section">
          <div className="section-header">
            <h2>Order History</h2>
            <div className="search-filter-container">
              {/* Search Bar */}
              <div className="search-bar">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  placeholder="Search by order number, product name, or color..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchQuery('')}
                    title="Clear search"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
              
              {/* Filter Dropdown */}
              <div className="filter-dropdown">
                <i className="fas fa-filter filter-icon"></i>
                <select 
                  value={activeFilter} 
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="filter-select"
                >
                  {filterOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="total-orders">
            <span>
              {ordersLoading ? 'Loading...' : ordersError ? 'Error loading orders' : 
                filteredOrders.length === userOrders.length 
                  ? `Total Orders: ${userOrders.length}` 
                  : `Showing ${filteredOrders.length} of ${userOrders.length} orders`
              }
            </span>
            {(searchQuery || activeFilter !== 'All Orders') && (
              <button 
                className="clear-filters"
                onClick={() => {
                  setSearchQuery('');
                  setActiveFilter('All Orders');
                }}
              >
                <i className="fas fa-times"></i>
                Clear filters
              </button>
            )}
          </div>

          {/* Orders List */}
          <div className="orders-list">
            {ordersLoading ? (
              <SiteLoading
                title="Loading Your Orders"
                subtitle="Fetching order history..."
                icon="📋"
                variant="inline"
              />
            ) : ordersError ? (
              <div className="orders-error" style={{
                padding: '40px',
                textAlign: 'center',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                color: '#856404'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '32px', marginBottom: '15px' }}></i>
                <h3>Error Loading Orders</h3>
                <p>{ordersError}</p>
                <button 
                  onClick={() => window.location.reload()} 
                  style={{
                    marginTop: '15px',
                    padding: '10px 20px',
                    background: '#ffc107',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Retry
                </button>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="no-orders">
                <div className="no-orders-icon">📋</div>
                <h3>No orders found</h3>
                <p>
                  {searchQuery || activeFilter !== 'All Orders' 
                    ? 'No orders match your search criteria.' 
                    : 'You haven\'t placed any orders yet.'}
                </p>
              </div>
            ) : (
              filteredOrders.map(order => (
                <div key={order.id} className="modern-order-card">
                  <div className="order-card-header">
                    <div className="order-number">
                      <span className="label">Order</span>
                      <span className="number">{order.orderNumber}</span>
                      <span 
                        className={`status-badge ${order.status.toLowerCase()}`}
                      >
                        {order.status}
                      </span>
                    </div>
                    <div className="order-date">
                      <span className="label">Placed on:</span>
                      <span>{new Date(order.date).toLocaleDateString('en-GB')}</span>
                      {order.deliveredDate && (
                        <>
                          <span className="label">Delivered on:</span>
                          <span>{new Date(order.deliveredDate).toLocaleDateString('en-GB')}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="order-card-content">
                    {order.items.map((item, index) => (
                      <div 
                        key={index} 
                        className="order-item-row"
                      >
                        <img src={item.image} alt={item.name} className="item-thumbnail" />
                        <div className="item-info">
                          <h4 className="item-name">{item.name}</h4>
                          <div className="item-details">
                            Size: {item.size} | Color: {item.color} | Qty: {item.quantity}
                          </div>
                        </div>
                        <div className="item-price">Rs. {item.price.toLocaleString()}</div>
                      </div>
                    ))}
                  </div>

                  <div className="order-card-footer">
                    <div className="order-total">
                      Rs. {order.total.toLocaleString()}
                    </div>
                    <div className="order-actions">
                      <button 
                        className="modern-action-btn track-btn"
                        onClick={() => handleTrackOrder(order)}
                      >
                        <div className="btn-icon">
                          <i className="fas fa-map-marker-alt"></i>
                        </div>
                        <div className="btn-content">
                          <span className="btn-title">Track Order</span>
                          <span className="btn-subtitle">Check status</span>
                        </div>
                      </button>
                      {/* Show reorder button for all orders */}
                      <button 
                        className="modern-action-btn reorder-btn"
                        onClick={() => handleReorder(order.items[0])}
                      >
                        <div className="btn-icon">
                          <i className="fas fa-shopping-cart"></i>
                        </div>
                        <div className="btn-content">
                          <span className="btn-title">Reorder</span>
                          <span className="btn-subtitle">Buy again</span>
                        </div>
                      </button>
                      {canDeleteOrder(order) && (
                        <button 
                          className="modern-action-btn delete-order-btn"
                          onClick={() => handleDeleteOrder(order)}
                          disabled={deletingOrderId === (order.id || order._id)}
                          title="Cancel this order (only available when status is Order Placed)"
                        >
                          <div className="btn-icon">
                            {deletingOrderId === (order.id || order._id) ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash-alt"></i>
                            )}
                          </div>
                          <div className="btn-content">
                            <span className="btn-title">Delete Order</span>
                            <span className="btn-subtitle">Cancel order</span>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>


                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={cancelRemoveItem}
        onConfirm={confirmRemoveItem}
        title="Remove Item"
        message="Are you sure you want to remove this item from your pending orders?"
      />

      {/* Tracking Modal */}
      <TrackingModal
        isOpen={showTrackingModal}
        onClose={closeTrackingModal}
        order={selectedOrder}
        orderStatusSystem={orderStatusSystem}
        loading={trackingOrderLoading}
      />
    </div>
  );
  };

export default OrdersPage; 